import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, { FadeIn, FadeOut, ZoomIn } from 'react-native-reanimated';

import { ModernPalette } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { getStickyNoteColor } from '@/constants/sticky-note-colors';
import type { AISuggestion } from '@/types/ai-suggestions';

type AddTaskStatus = 'idle' | 'loading' | 'success' | 'error';

type SuggestionDetailOverlayProps = {
  suggestion: AISuggestion | null;
  onClose: () => void;
  onAddToCalendar?: (suggestion: AISuggestion) => void;
  onAddToTodo?: (suggestion: AISuggestion) => Promise<boolean>;
};

/**
 * SuggestionDetailOverlay - Full-detail card overlay for viewing a suggestion.
 * Transparent modal with darkened backdrop and centered sticky-note card.
 */
export function SuggestionDetailOverlay({
  suggestion,
  onClose,
  onAddToCalendar,
  onAddToTodo,
}: SuggestionDetailOverlayProps) {
  const [addTaskStatus, setAddTaskStatus] = useState<AddTaskStatus>('idle');
  const [isExpanded, setIsExpanded] = useState(false);

  if (!suggestion) return null;

  const backgroundColor = getStickyNoteColor(suggestion.id);
  const isAddDisabled = addTaskStatus !== 'idle';
  const hasMore = (suggestion.actions?.length ?? 0) > 0;

  const handleAddToTodo = async () => {
    if (!onAddToTodo || isAddDisabled) return;
    setAddTaskStatus('loading');
    const success = await onAddToTodo(suggestion);
    setAddTaskStatus(success ? 'success' : 'error');
  };

  return (
    <Modal
      visible={!!suggestion}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.container}>
        {/* Backdrop */}
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(150)}
          style={styles.backdrop}
        >
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>

        {/* Card */}
        <Animated.View
          entering={FadeIn.duration(250).delay(50)}
          style={[styles.card, { backgroundColor }]}
        >
          {/* Close button */}
          <TouchableOpacity
            onPress={onClose}
            style={styles.closeButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityLabel="Close detail"
          >
            <Ionicons name="close" size={22} color="#999" />
          </TouchableOpacity>

          {/* Sparkle + Title */}
          <View style={styles.header}>
            <Ionicons name="sparkles" size={22} color="#555" />
            <Text style={[styles.title, { fontFamily: getBodyFont() }]}>
              {suggestion.title}
            </Text>
          </View>

          {/* Description (truncated until expanded) */}
          <Text
            style={[styles.description, { fontFamily: getBodyFont() }]}
            numberOfLines={isExpanded ? undefined : 3}
          >
            {suggestion.description}
          </Text>

          {/* Action bullets — only when expanded */}
          {isExpanded && hasMore && (
            <View style={styles.actionsList}>
              {suggestion.actions!.map((action, idx) => (
                <View key={`${action}-${idx}`} style={styles.actionRow}>
                  <Text style={[styles.actionBullet, { fontFamily: getBodyFont() }]}>•</Text>
                  <Text style={[styles.actionText, { fontFamily: getBodyFont() }]}>{action}</Text>
                </View>
              ))}
            </View>
          )}

          {/* See more / See less toggle */}
          {hasMore && (
            <TouchableOpacity
              onPress={() => setIsExpanded((prev) => !prev)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityLabel={isExpanded ? 'See less' : 'See more'}
            >
              <Text style={[styles.seeMoreText, { fontFamily: getBodyFont() }]}>
                {isExpanded ? 'See less' : 'See more'}
              </Text>
            </TouchableOpacity>
          )}

          {/* Action buttons */}
          {(onAddToTodo || onAddToCalendar) && (
            <View style={styles.footer}>
              {onAddToTodo && (
                <DetailAddTaskButton
                  status={addTaskStatus}
                  onPress={handleAddToTodo}
                  disabled={isAddDisabled}
                />
              )}
              {onAddToCalendar && (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => {
                    onAddToCalendar(suggestion);
                    onClose();
                  }}
                  activeOpacity={0.8}
                  accessibilityLabel="Add to calendar"
                >
                  <Ionicons name="calendar-outline" size={18} color={ModernPalette.textInverse} />
                  <Text style={[styles.actionButtonText, { fontFamily: getBodyFont() }]}>
                    Add Event
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </Animated.View>
      </View>
    </Modal>
  );
}

type DetailAddTaskButtonProps = {
  status: AddTaskStatus;
  onPress: () => void;
  disabled: boolean;
};

function DetailAddTaskButton({ status, onPress, disabled }: DetailAddTaskButtonProps) {
  const getButtonStyle = () => {
    switch (status) {
      case 'success': return [styles.actionButton, styles.actionButtonSuccess];
      case 'error': return [styles.actionButton, styles.actionButtonError];
      default: return styles.actionButton;
    }
  };

  return (
    <TouchableOpacity
      style={getButtonStyle()}
      onPress={onPress}
      activeOpacity={0.8}
      disabled={disabled}
      accessibilityLabel="Add to todo list"
    >
      {status === 'loading' && (
        <ActivityIndicator size="small" color={ModernPalette.textInverse} />
      )}
      {status === 'success' && (
        <Animated.View entering={ZoomIn.duration(200)}>
          <Ionicons name="checkmark" size={18} color={ModernPalette.textInverse} />
        </Animated.View>
      )}
      {status === 'error' && (
        <Animated.View entering={FadeIn.duration(200)}>
          <Ionicons name="alert-circle" size={18} color={ModernPalette.textInverse} />
        </Animated.View>
      )}
      {status === 'idle' && (
        <Ionicons name="add" size={18} color={ModernPalette.textInverse} />
      )}
      <Text style={[styles.actionButtonText, { fontFamily: getBodyFont() }]}>
        {status === 'success' ? 'Added!' : status === 'error' ? 'Failed' : 'Add Task'}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Spacing.xl,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  card: {
    width: '100%',
    borderRadius: BorderRadius.lg,
    padding: Spacing.xl,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8,
    gap: Spacing.md,
  },
  closeButton: {
    position: 'absolute',
    top: Spacing.md,
    right: Spacing.md,
    zIndex: 1,
    padding: Spacing.xxs,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingRight: Spacing.xl,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#333',
    flex: 1,
  },
  description: {
    fontSize: 15,
    lineHeight: 22,
    color: '#555',
  },
  actionsList: {
    gap: Spacing.xxs,
    marginTop: Spacing.xs,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.xs,
  },
  actionBullet: {
    fontSize: 14,
    lineHeight: 20,
    color: '#555',
    width: 10,
  },
  actionText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#555',
    flex: 1,
  },
  seeMoreText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
    textDecorationLine: 'underline',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: Spacing.xs,
    gap: Spacing.sm,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: Spacing.md,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#333',
  },
  actionButtonSuccess: {
    backgroundColor: '#2E7D32',
  },
  actionButtonError: {
    backgroundColor: '#C62828',
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: ModernPalette.textInverse,
  },
});
